import styled from '@emotion/styled';
import { useLocalizationContext } from '@eventbrite/create-common';
import {
    MoreOptionsLocationFields,
    StaticMap,
    useFormValues,
    useRegions,
    useTimezone,
    venueHasEnoughData,
    VenueValues,
    verifyVenueRegion,
} from '@eventbrite/event-create-ui';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { AutoCreateEventFormValues } from '../../types';
import { VenueSearch } from './VenueSearch';

export enum VenueLocationTypeTestIds {
    Container = 'VenueContainer',
    Switch = 'VenueSwitch',
    Label = 'VenueSwitchLabel',
}

export const VenueLocationType = () => {
    const { venue, showVenueOptions } =
        useFormValues<AutoCreateEventFormValues>();
    const { setValue } = useFormContext<AutoCreateEventFormValues>();
    const { fetch: fetchTimezone } = useTimezone();
    const { data: regions = [] } = useRegions();
    const [isValidRegion, setIsValidRegion] = React.useState(true);
    const { updatePlace } = useLocalizationContext();

    const onChange = useCallback(
        async (venue: VenueValues) => {
            const timezone = await fetchTimezone(venue);
            const { region, country } = venue.address;
            const isValidRegion = verifyVenueRegion({
                region,
                country,
                regions,
            });
            setIsValidRegion(isValidRegion);
            const enoughDataForVenue = venueHasEnoughData(venue);
            const values = {
                ...venue,
                address: {
                    ...venue.address,
                    region: isValidRegion ? region : '',
                },
            };

            updatePlace(values, timezone);
            setValue('venue', values, { shouldDirty: true });
            if (!isValidRegion || !enoughDataForVenue) {
                setValue('showVenueOptions', true);
            }
        },
        [fetchTimezone, regions, setValue, updatePlace],
    );

    return (
        <div data-testid={VenueLocationTypeTestIds.Container}>
            <Container>
                {!showVenueOptions && <VenueSearch onChange={onChange} />}

                <MoreOptionsLocationFields
                    isValidRegion={isValidRegion}
                    onGeocoding={() => undefined}
                />
            </Container>

            <StaticMap
                latitude={venue.latitude}
                longitude={venue.longitude}
                showMarker={!!venue.id}
                location={venue.address.localizedAddress}
            />
        </div>
    );
};

const Container = styled.div`
    margin-bottom: 1.25rem;
`;
